function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import Highcharts from 'highcharts';
import clsx from 'clsx';
import { generatePointTooltipParams, getLineChartData, getLineSeries, getOtherSegmentData, LineChartData, OtherSegmentData } from 'common/report/insights/charts/insights-line-chart/util';
import { PositionParent } from 'common/components/drop-menu';
import { IChartDataItem, LineChartYAxisOptions, XAxisOptions } from 'common/charts/visualization/line-chart/types';
import { ParamsAndResults } from 'common/report/types';
import { InsightsQueryResults } from 'common/report/insights/util/types';
import { ChartType, InsightsBookmarkParams, SortConfig } from 'common/types/reports/bookmark';
import { PopoverCallback } from 'common/charts/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { CurrentProject, CurrentProjectResource } from 'common/report/model-resources/projects';
import { getWorkspaceId, isPublicMode } from 'common/report/util';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { ProjectTimezone } from 'common/report/stores/types';
import { canShowOtherSegment, getSortConfig, insightsBookmarkParamsToChartParams } from 'common/report/insights/util';
import { INSIGHTS_LINE_CSV_EXPORT_LIMIT, REPORT_LIMITS, ReportName } from 'common/report/constants';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { renderLineChartTooltip, RenderLineChartTooltipParams, ShowClauseData } from 'common/report/insights/util/tooltip';
import { OTHER_SEGMENT_VALUE } from 'common/report/insights/util/constants';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { deselectPoint, selectPoint } from 'common/report/util/highcharts';
import { getDisplayOptionsFromLineChartYAxisOptions, getMetricFormattingDefaultsFromChartParams, mergeDisplayOptions, metricFormatterFunctionForAxis } from 'common/report/insights/charts/util';
import { getDefaultPrimaryAndSecondaryAxisOptions, getYAxisDefaultOptions } from 'common/query-builder/chart-customization/utils';
import { formatMetricNumberWithUnits } from 'common/util';
import { SpinnerReact } from 'common/components/spinner';
import { colors } from 'common/util/colors';
import { TimeSeriesType } from 'common/charts/visualization/line-chart/enums';
import { renamePropertyValue } from 'common/report/util/properties';
import { ContextMenuReact } from 'common/widgets/context-menu';
import { useTranslation } from 'common/i18n/hooks';
import { SegmentLegendHeader } from 'common/charts/misc/segment-legend-header';
import { LineChartReact } from 'common/charts/visualization/line-chart';
import { isRetentionOnlyInsightsBookmark } from 'common/report/insights/models/utils';
import { EmptyDataReact } from 'common/charts/misc/empty-data';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
import { IronContextRegistry } from 'common/report/context-registry';
import { withStyles } from 'common/util/react';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
const LEFT_MARGIN_WITHOUT_YAXIS = 25;
export type OnPopoverActionProps = {
  detail: {
    action: string;
    pointData: RenderLineChartTooltipParams;
  };
};
export type InsightsLineChartProps = {
  elref?: string;
  allowChartUpdate?: boolean;
  segmentFilterController: SegmentFilterController;

  // use a single prop for bookmark params and results so they can be updated together atomically
  paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;
  popoverCallback?: PopoverCallback;
  dataGroups?: Record<string, DataGroup>;
  formatXAxisLabel?: (value: number, timeUnit?: TimeUnit) => string;
  disableTooltip?: boolean;
  disableZoom?: boolean;
  failed?: boolean;
  hideChartLines?: boolean;
  hideLegend?: boolean;
  loaded: boolean;
  showAnnotations?: boolean;
  showFirstLastTick?: boolean;
  timezone?: string;
  utcOffset?: number;
  xAxisOptions?: XAxisOptions;
  primaryYAxisOptions?: Highcharts.YAxisOptions;
  secondaryYAxisOptions?: Highcharts.YAxisOptions;
  emptyDataContainerClassName?: string;
  onPopoverAction?: (props: OnPopoverActionProps) => void;
  onHighchartMouseOver?: ({
    tooltipParams,
    chartContainer,
    plotLeft,
    plotTop,
    plotX,
    plotY
  }: {
    tooltipParams: RenderLineChartTooltipParams;
    chartContainer: DOMRect;
    plotLeft: number;
    plotTop: number;
    plotX: number;
    plotY: number;
  }) => void;
  onHighchartMouseOut?: () => void;
};
export const InsightsLineChart: React.FC<Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`> & InsightsLineChartProps> = props => {
  var _props$primaryYAxisOp;
  const [lineChartData, setLineChartData] = useState<LineChartData | null>(null);
  /** clicked highcharts point object to display a popover for */
  const [popoverSelection, setPopoverSelection] = useState<Highcharts.Point | null>(null);
  /** holds positioning of the popover for the active highcharts point */
  const [popoverPositionParent, setPopoverPositionParent] = useState<PositionParent>();
  const [project, setProject] = useState<CurrentProject | null>(null);
  const [projectTimezone, setProjectTimezone] = useState<ProjectTimezone | null>(null);
  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  const [dataGroups, setDataGroups] = useState<Record<string, DataGroup> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [paramsAndResults, setParamsAndResults] = useState<ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null>(null);
  const bookmarkParams = paramsAndResults === null || paramsAndResults === void 0 ? void 0 : paramsAndResults.params;
  const results = paramsAndResults === null || paramsAndResults === void 0 ? void 0 : paramsAndResults.results;
  const hasProjectTimeAttributes = (): boolean => {
    return Boolean(props.timezone);
  };
  const getCurrentProject = (): CurrentProject | undefined => {
    return isPublicMode() ? undefined : props.currentProject.get().value;
  };
  const getProjectTimezone = (): ProjectTimezone | null => {
    if (hasProjectTimeAttributes()) {
      return {
        utcOffset: props.utcOffset,
        timezone: props.timezone
      };
    }
    return projectTimezone;
  };
  const hasRequiredParams = (): boolean => {
    // Props may have been set before connectedCallback() fires, therefore need to ensure
    // this component is connected before accessing a context (e.g. props.segmentFilterController)
    return !!(isConnected && getProjectTimezone() && bookmarkParams && results);
  };
  const getDataGroups = (): Record<string, DataGroup> => {
    var _dataGroupStore$value;
    if (dataGroups) {
      return dataGroups;
    }
    return (_dataGroupStore$value = dataGroupStore.value) !== null && _dataGroupStore$value !== void 0 ? _dataGroupStore$value : {};
  };
  const updateTimezoneFromContext = () => {
    // check public dashboard
    if (!dataGroups) {
      const currentProject = getCurrentProject();
      if (currentProject) {
        setProjectTimezone({
          utcOffset: currentProject.utcOffset,
          timezone: currentProject.timezone
        });
      }
    }
  };
  const updateLineChartData = (): void => {
    if (hasRequiredParams()) {
      const newLineChartData = getLineChartData(getProjectTimezone(),
      // extracting only the actual values used allows for better memoization
      insightsBookmarkParamsToChartParams(bookmarkParams), results, getDataGroups(), props.metrics, props.i18n);
      setLineChartData(newLineChartData);
      const {
        meta
      } = results;
      const {
        apiResultsTruncated,
        queryLimits
      } = meta;
      props.segmentFilterController.loadSections([{
        items: newLineChartData.legendSegmentItems,
        metadata: newLineChartData.legendIdToIndexMap,
        // Insights app is expected to use this mapping to persist legend selection indices to the URL
        apiResultsTruncated,
        tableColumns: newLineChartData.tableColumns,
        readOnlyItems: newLineChartData.readOnlyLegendItems
      }], {
        defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[bookmarkParams.displayOptions.chartType].default,
        maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[bookmarkParams.displayOptions.chartType].max,
        tableSortConfig: getSortConfig({
          bookmarkSortConfig: bookmarkParams.sorting,
          headerCount: results.headers.length
        })[ChartType.Line] as SortConfig // Column chart shares sort config with line
      });
      props.segmentFilterController.segmentationQueryLimit = queryLimits === null || queryLimits === void 0 ? void 0 : queryLimits.limit;
      props.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(getCurrentProject()) ? INSIGHTS_LINE_CSV_EXPORT_LIMIT : REPORT_LIMITS[ReportName.Insights].csvExport;
    } else {
      setLineChartData(null);
    }
  };
  const isEndDateIncomplete = () => {
    var _lineChartData$lineCh;
    if (!lineChartData.lineChartSeries.length) {
      return false;
    }
    const hasIncompleteData = (_lineChartData$lineCh = lineChartData.lineChartSeries[0].incompleteLength) !== null && _lineChartData$lineCh !== void 0 ? _lineChartData$lineCh : 0;
    return isRetentionOnlyInsightsBookmark(bookmarkParams) && hasIncompleteData > 0;
  };
  const hasForecastComparison = () => {
    var _lineChartData$foreca;
    return ((_lineChartData$foreca = lineChartData.forecastComparisonSegmentLookup) === null || _lineChartData$foreca === void 0 ? void 0 : _lineChartData$foreca.size) > 0;
  };
  const updatePersistedLegendSelections = () => {
    var _bookmarkParams$legen;
    const individualSelectedSegments = bookmarkParams === null || bookmarkParams === void 0 || (_bookmarkParams$legen = bookmarkParams.legend) === null || _bookmarkParams$legen === void 0 ? void 0 : _bookmarkParams$legen.individualSelectedSegments;
    if (isConnected && individualSelectedSegments) {
      props.segmentFilterController.loadFromSavedLegend(individualSelectedSegments ? [individualSelectedSegments] : null);
    }
  };
  const resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => {
    // this component disables project & dataGroup Stores if equivalent attributes are specified, this may be required
    // in environments where Stores don't work, eg. Public Dashboards
    if (!dataGroups && !hasProjectTimeAttributes()) {
      const currentProject = getCurrentProject();
      if ((project === null || project === void 0 ? void 0 : project.id) !== currentProject.id) {
        const projectId = currentProject.id;
        const workspaceId = parseInt(getWorkspaceId(), 10);
        // Note: data groups are only used in tooltips and (as of writing) do not need to trigger state updates/renders or annotated with people properties
        dataGroupStore.fetch({
          projectId,
          workspaceId
        }); // async, no need to wait
      }
      if (!isEqual(project, currentProject)) {
        setProject(currentProject);
        updateTimezoneFromContext();
        updateLineChartData();
      }
    }
  }]]);
  useEffect(() => {
    updateTimezoneFromContext();
    props.currentProject.subscribe(resourceCustomSubscriptions.get(CurrentProjectResource));
    setIsConnected(true);
    return () => {
      props.currentProject.unsubscribe(resourceCustomSubscriptions.get(CurrentProjectResource));
    };
  }, []);
  useEffect(() => {
    if (!isEqual(paramsAndResults, props.paramsAndResults)) {
      setParamsAndResults(props.paramsAndResults);
    }
  }, [props.paramsAndResults]);
  useEffect(() => {
    if (paramsAndResults) {
      updateLineChartData();
      updatePersistedLegendSelections();
    }
  }, [paramsAndResults, dataGroups]);
  useEffect(() => {
    setDataGroups(props.dataGroups);
  }, [props.dataGroups]);
  const formatYAxisLabel = (y: number, axis: BookmarkTypes.AxisAssignment, yAxisOptions?: LineChartYAxisOptions): string => {
    const defaultOptions = getMetricFormattingDefaultsFromChartParams(bookmarkParams, true);
    const {
      defaultPrimaryOptions,
      defaultSecondaryOptions
    } = getDefaultPrimaryAndSecondaryAxisOptions(bookmarkParams, props.metrics);
    const preFormatted = metricFormatterFunctionForAxis(bookmarkParams, axis)(y);
    const formatted = formatMetricNumberWithUnits(preFormatted, mergeDisplayOptions({}, defaultOptions, getDisplayOptionsFromLineChartYAxisOptions(axis === BookmarkTypes.AxisAssignment.Primary ? defaultPrimaryOptions : defaultSecondaryOptions), getDisplayOptionsFromLineChartYAxisOptions(yAxisOptions)), {
      abbreviateThousands: true
    });
    return formatted;
  };
  const getPointDataFromSelection = () => {
    if (popoverSelection === null) {
      return null;
    }
    const point = popoverSelection;
    return generatePointTooltipParams({
      highchartsContext: {
        point,
        series: point.series,
        percentage: point.percentage
      },
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(getDataGroups()),
      projectTimezone: getProjectTimezone(),
      metrics: props.metrics,
      i18n: props.i18n
    });
  };
  const handlePopoverClose = () => {
    const selectedPoint = popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    setPopoverSelection(null);
    setPopoverPositionParent(null);
  };
  const handlePopoverAction = (action: string) => {
    var _props$onPopoverActio;
    (_props$onPopoverActio = props.onPopoverAction) === null || _props$onPopoverActio === void 0 || _props$onPopoverActio.call(props, {
      detail: {
        action,
        pointData: getPointDataFromSelection()
      }
    });
    handlePopoverClose();
  };

  // Arrow syntax is required to bind "this", which is otherwise changed by highcharts
  const handlePointClick = (point: Highcharts.Point, event: Highcharts.PointClickEventObject) => {
    var _props$popoverCallbac;
    const {
      series
    } = point;
    if (series.options.name === OTHER_SEGMENT_VALUE) {
      return;
    }
    const pointData = generatePointTooltipParams({
      highchartsContext: {
        point,
        series,
        percentage: point.percentage
      },
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(getDataGroups()),
      projectTimezone: getProjectTimezone(),
      metrics: props.metrics,
      i18n: props.i18n
    });
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    const hasPopoverActions = (_props$popoverCallbac = props.popoverCallback) === null || _props$popoverCallbac === void 0 ? void 0 : _props$popoverCallbac.call(props, metricKey, pointData.segments, {
      highchartsPointContext: point,
      pointData
    }).length;
    if (hasPopoverActions) {
      selectPoint(point);
      setPopoverPositionParent({
        getBoundingClientRect: () => {
          var _point$graphic$elemen, _point$graphic, _markerGroup;
          const isVerticalBar = bookmarkParams.displayOptions.chartType === ChartType.Column;
          const targetEl: HTMLElement = (_point$graphic$elemen = (_point$graphic = point.graphic) === null || _point$graphic === void 0 ? void 0 : _point$graphic.element) !== null && _point$graphic$elemen !== void 0 ? _point$graphic$elemen : (_markerGroup = (point.series as any).markerGroup) === null || _markerGroup === void 0 || (_markerGroup = _markerGroup.element) === null || _markerGroup === void 0 ? void 0 : _markerGroup.firstChild;
          const targetRect = targetEl === null || targetEl === void 0 ? void 0 : targetEl.getBoundingClientRect();

          // close the popover when the line chart is resized and the selected point marker goes away
          if (targetRect === undefined) {
            handlePopoverClose();
            return new DOMRect(0, 0, 0, 0);
          } else if (isVerticalBar) {
            // makes sure the popover shifts over the Y axis to align it with the mouse cursor
            // this improves accessing the menu on larger columns
            return new DOMRect(targetRect.left, event.pageY, targetRect.width, targetRect.height);
          }
          return targetRect;
        }
      });
      setPopoverSelection(point);
    }
  };
  const handleHighchartMouseOver = (_ref: {
    highchartPoint: Highcharts.Point;
    highchartChart: Highcharts.Chart;
  }) => {
    var _highchartPoint$perce, _props$onHighchartMou;
    let {
      highchartPoint,
      highchartChart
    }: {
      highchartPoint: Highcharts.Point;
      highchartChart: Highcharts.Chart;
    } = _ref;
    const tooltipParams = generatePointTooltipParams({
      highchartsContext: {
        point: highchartPoint,
        series: highchartPoint.series,
        percentage: (_highchartPoint$perce = highchartPoint.percentage) !== null && _highchartPoint$perce !== void 0 ? _highchartPoint$perce : 0
      },
      bookmarkParams,
      results,
      lineChartData,
      dataGroups: Object.values(getDataGroups()),
      projectTimezone,
      metrics: props.metrics,
      i18n: props.i18n
    });
    (_props$onHighchartMou = props.onHighchartMouseOver) === null || _props$onHighchartMou === void 0 || _props$onHighchartMou.call(props, {
      tooltipParams,
      chartContainer: highchartChart.container.getBoundingClientRect(),
      // container’s on‐screen position
      plotLeft: highchartChart.plotLeft,
      plotTop: highchartChart.plotTop,
      plotX: highchartPoint.plotX,
      plotY: highchartPoint.plotY
    });
  };
  const handleHighchartMouseOut = () => {
    var _props$onHighchartMou2;
    (_props$onHighchartMou2 = props.onHighchartMouseOut) === null || _props$onHighchartMou2 === void 0 || _props$onHighchartMou2.call(props);
  };
  const getPopoverMenuItems = () => {
    const pointData = getPointDataFromSelection();
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    return props.popoverCallback(metricKey, pointData.segments, {
      highchartsPointContext: popoverSelection,
      isTimeComparisonSegment: pointData.isComparisonItem,
      pointData
    });
  };
  const loaded = props.loaded && hasRequiredParams() && lineChartData;
  if (!loaded) {
    return <div className={styles.root}>
        <SpinnerReact className={styles.mpSpinner} elref={"loadingSpinner"} />
      </div>;
  }
  if (isEmpty(results === null || results === void 0 ? void 0 : results.series)) {
    return <div className={"".concat(styles.root, " ").concat(props.emptyDataContainerClassName)}>
        <EmptyDataReact />
      </div>;
  }

  // There will be multiple segments with the same segment name when using time comparison
  const seriesLookup = new Map<string, IChartDataItem[]>();
  lineChartData.lineChartSeries.forEach(segment => {
    const segmentKey = segment.name;
    if (!seriesLookup.has(segmentKey)) {
      seriesLookup.set(segmentKey, []);
    }
    seriesLookup.get(segmentKey).push(segment);
  });
  const visibleChartSeries: IChartDataItem[] = props.segmentFilterController.getSelectedItems(0).flatMap(item => {
    return seriesLookup.get(item.id).map(segment => _objectSpread(_objectSpread({}, segment), {}, {
      color: props.segmentFilterController.getItemColor(0, item.id)
    }));
  });

  // include other segment in rendering cycle rather than data processing cycle
  // so that we have accurate values on each segment selection
  let otherSegmentData: OtherSegmentData;
  if (canShowOtherSegment(bookmarkParams)) {
    const allSegments = Array.from(lineChartData.segmentLookup.values());
    otherSegmentData = getOtherSegmentData(allSegments, props.segmentFilterController);
    if (!otherSegmentData.isAllZero) {
      visibleChartSeries.unshift(getLineSeries(otherSegmentData.percentagesSegment, colors.otherSegment.primary, getProjectTimezone(), results.meta.report_sections.show[0]));
    }
  }
  const visibleSegmentIds = visibleChartSeries.map(segment => {
    return segment.name;
  });
  const bookmarkPrimaryYAxisOptions = bookmarkParams.displayOptions.primaryYAxisOptions;
  const metricsResource = props.metrics;
  const defaultPrimaryYAxisOptions = getYAxisDefaultOptions(bookmarkParams, BookmarkTypes.AxisAssignment.Primary, metricsResource);
  const defaultSecondaryYAxisOptions = getYAxisDefaultOptions(bookmarkParams, BookmarkTypes.AxisAssignment.Secondary, metricsResource);
  const bookmarkSecondaryYAxisOptions = bookmarkParams.displayOptions.secondaryYAxisOptions;
  const bookmarkXAxisOptions = bookmarkParams.displayOptions.xAxisOptions;
  return <div className={clsx(styles.root, "insights-line-chart")} elref={props.elref} data-sentry-component="InsightsLineChart" data-sentry-source-file="index.tsx">
      {!props.hideLegend && <SegmentLegendHeader className={styles.segmentLegendHeader} visibleSegmentIds={visibleSegmentIds} segmentFilterController={props.segmentFilterController} />}
      <LineChartReact className={styles.lineChart} allowChartUpdate={props.allowChartUpdate} segmentFilterController={props.segmentFilterController} chartData={visibleChartSeries} chartMarginLeft={((_props$primaryYAxisOp = props.primaryYAxisOptions) === null || _props$primaryYAxisOp === void 0 ? void 0 : _props$primaryYAxisOp.visible) === false ? LEFT_MARGIN_WITHOUT_YAXIS : undefined} displayAnalysisType={lineChartData.chartAnalysisType} displayPlotStyle={lineChartData.chartPlotStyle} displaySeriesType={bookmarkParams.displayOptions.chartType === ChartType.Column ? TimeSeriesType.Column : TimeSeriesType.Line} displayStackingType={lineChartData.chartStackingType} disableAreaRangeMouseTracking={hasForecastComparison()} {...props.showFirstLastTick && {
      formatTicksX: (min, max) => {
        return [min, max];
      }
    }} disableTooltip={props.disableTooltip} formatTooltip={(_x, _y, _series, _seriesIdx, tooltipContext): string => {
      var _getProjectTimezone;
      const tooltipParams = generatePointTooltipParams({
        highchartsContext: tooltipContext,
        bookmarkParams,
        results,
        lineChartData,
        dataGroups: Object.values(getDataGroups()),
        projectTimezone: getProjectTimezone(),
        otherSegmentData,
        metrics: props.metrics,
        i18n: props.i18n
      });
      return renderLineChartTooltip(_objectSpread(_objectSpread({}, tooltipParams), {}, {
        segments: tooltipParams.segments.map((value, index) => renamePropertyValue(value, results.headers[index + 1], bookmarkParams.sections.group[index])),
        timezone: (_getProjectTimezone = getProjectTimezone()) === null || _getProjectTimezone === void 0 ? void 0 : _getProjectTimezone.timezone,
        i18n: props.i18n,
        metricsResource: props.metrics
      }));
    }} formatYAxisLabel={formatYAxisLabel} hideChartLines={props.hideChartLines} incompleteToDate={isEndDateIncomplete()} onHighChartClick={props.popoverCallback && ((a, b) => handlePointClick(a, b))} showAnnotations={props.showAnnotations} timeUnit={lineChartData.chartTimeUnit} timezone={getProjectTimezone().timezone} utcOffset={getProjectTimezone().utcOffset} defaultPrimaryYAxisOptions={defaultPrimaryYAxisOptions} defaultSecondaryYAxisOptions={defaultSecondaryYAxisOptions} primaryYAxisOptions={_objectSpread(_objectSpread({}, bookmarkPrimaryYAxisOptions), props.primaryYAxisOptions)} secondaryYAxisOptions={_objectSpread(_objectSpread({}, bookmarkSecondaryYAxisOptions), props.secondaryYAxisOptions)} zoomType={!props.disableZoom ? "x" : undefined} xAxisOptions={_objectSpread(_objectSpread({}, props.xAxisOptions), bookmarkXAxisOptions)} {...props.formatXAxisLabel && {
      formatXAxisLabel: props.formatXAxisLabel
    }} onHighchartMouseOver={handleHighchartMouseOver} onHighchartMouseOut={handleHighchartMouseOut} data-sentry-element="LineChartReact" data-sentry-source-file="index.tsx" />
      {/* render point popover */}
      {popoverSelection && <>
          <ContextMenuReact className={styles.mpContextMenu} relativePositioning open position={{
        left: 0,
        top: 0
      }} onClose={() => handlePopoverClose()} onAction={ev => handlePopoverAction(ev.detail.action)} positionParent={popoverPositionParent} menuItems={getPopoverMenuItems()} />
        </>}
    </div>;
};
export const InsightsLineChartReact = withStyles((props: InsightsLineChartProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsLineChart currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);